import React from 'react';
import { Typography, Card, Steps } from 'antd';

const { Title, Paragraph, Text } = Typography;

function Documentation() {
  return (
    <Card style={{ marginBottom: 24 }}>
      <Typography>
        <Title level={3}>What is Scrum Poker?</Title>
        <Paragraph>
          Scrum Poker, also called Planning Poker, is a consensus-based estimation technique used by agile teams. 
          Team members make estimates by playing numbered cards face-down instead of speaking them aloud, 
          avoiding the cognitive bias of anchoring, where the first estimate spoken aloud can influence others.
        </Paragraph>

        <Title level={4}>Why use Scrum Poker?</Title>
        <Paragraph>
          <ul>
            <li>Avoids anchoring bias from influential team members</li>
            <li>Encourages participation from all team members</li>
            <li>Leads to more accurate estimates through discussion</li>
            <li>Makes estimation more engaging and fun</li>
          </ul>
        </Paragraph>

        <Title level={4}>How to use this tool</Title>
        <Steps
          direction="vertical"
          items={[
            {
              title: 'Create or Join a Session',
              description: 'Create a new session and invite your team members using the invite link, or join an existing session.'
            },
            {
              title: 'Add User Stories',
              description: 'The session creator can add user stories to estimate, or use Quick Vote for immediate voting.'
            },
            {
              title: 'Vote',
              description: 'When voting starts, each team member selects their estimate. Votes remain hidden until everyone has voted.'
            },
            {
              title: 'Discuss',
              description: 'Once all votes are revealed, discuss any significant differences in estimates to reach consensus.'
            }
          ]}
        />

        <Title level={4}>Estimation Types</Title>
        <Paragraph>
          <ul>
            <li>
              <Text strong>Fibonacci (1, 2, 3, 5, 8, 13, 21): </Text> 
              Most common. Reflects the inherent uncertainty in estimating larger items.
            </li>
            <li>
              <Text strong>T-Shirt Sizes (XS, S, M, L, XL, XXL): </Text>
              Simple relative sizing, good for rough estimates.
            </li>
            <li>
              <Text strong>Linear (1-8): </Text>
              Traditional linear scale, good for small, well-understood tasks.
            </li>
            <li>
              <Text strong>Hours: </Text>
              Direct time estimates, useful when precise timing is needed.
            </li>
          </ul>
        </Paragraph>

        <Title level={4}>Best Practices</Title>
        <Paragraph>
          <ul>
            <li>Vote simultaneously to avoid influence from others</li>
            <li>Focus on relative size rather than exact time</li>
            <li>Discuss significant estimate differences</li>
            <li>Remember that estimates are not commitments</li>
            <li>Use the tool that best fits your team's needs</li>
          </ul>
        </Paragraph>
      </Typography>
    </Card>
  );
}

export default Documentation; 